// Download CSV choices for Finance Pro Bills. The export command posts this request and saves the file.
// Approved is not a field: the export read already limits rows to approved bills.
import { computed } from '@angular/core';
import { ActivityEntityName, DateUtility, Entity, ExportFormat, InvoicingCSVDownloadRequest, MetadataEntityFieldEditorType, MetadataEntityFieldType, SurfaceType } from '@jms/model';
import type { WorkBuddyDialogApi } from '@workbuddy/ui/v2/blocks/dialog';
import { defineSurface, defineSurfaceRuntime, defineSurfaceSchema, openFormSurfaceDialog } from '@workbuddy/ui/v2/surface.engine';
import { billCalendarIso } from '../manage/bill.dates';

interface BillExportFormModel {
  exportFormat: ExportFormat;
  fromDate: Date | string;
  toDate: Date | string;
  company: Entity | null;
  contacts: Entity[];
  excludeAmountZero: boolean;
  isTaxInclusiveFlag: boolean;
}

interface BillExportDialogApi {
  close: (request: InvoicingCSVDownloadRequest) => void;
  dismiss: () => void;
}

const FORMAT_OPTIONS = [
  { value: ExportFormat.MYOB, label: 'MYOB' },
  { value: ExportFormat.Xero, label: 'Xero' },
];

export function billExportFormDefinition(api: BillExportDialogApi) {

  return defineSurface<BillExportFormModel>({
    schema: billExportSchema(),
    runtime: billExportRuntime(),
    initialModel: defaultBillExportModel(),
    onCancel: () => api.dismiss(),
    onSubmit: event => {
      const request = billExportRequest(event.model);
      api.close(request);
      return request;
    },
  });
}

export function openBillExportDialog(dialogs: WorkBuddyDialogApi): Promise<InvoicingCSVDownloadRequest | undefined> {

  return openFormSurfaceDialog(dialogs, {
    title: 'Download CSV',
    width: 'min(560px, calc(100vw - 40px))',
    ariaLabel: 'Download CSV',
    createDefinition: billExportFormDefinition,
  });
}

function billExportSchema() {

  return defineSurfaceSchema(ActivityEntityName.Bill, SurfaceType.Form)
    .model<BillExportFormModel>()
    .submitLabel('Download')
    // Default field grid. PropertyList is the detail rail: zero row gap and right-aligned values.
    .body(body => body
      .section('format', section => section
        .columns(1)
        .field({ bind: 'exportFormat', required: true, autofocus: true, metadata: { displayName: 'Export Format', type: MetadataEntityFieldType.Enum, editor: MetadataEntityFieldEditorType.ButtonSelect, options: FORMAT_OPTIONS } }))
      .section('download', section => section
        .columns(2)
        .field({ bind: 'fromDate', required: true, metadata: { displayName: 'From Date', type: MetadataEntityFieldType.Date }, editorInputs: 'bill.exportFromDate' })
        .field({ bind: 'toDate', required: true, metadata: { displayName: 'To Date', type: MetadataEntityFieldType.Date }, editorInputs: 'bill.exportToDate' })
        .field({ bind: 'company', colspan: 2, metadata: { displayName: 'Billing Company', type: MetadataEntityFieldType.Lookup, entity: ActivityEntityName.Company, placeholder: '<Billing Company>' }, editorInputs: { placeholder: '<Billing Company>' } })
        .field({ bind: 'contacts', colspan: 2, metadata: { displayName: 'Contractors', type: MetadataEntityFieldType.Lookup, arrayOf: MetadataEntityFieldType.Lookup, entity: ActivityEntityName.Contractor }, editorInputs: { placeholder: '<Contractor(s)>' } })
        .field({ bind: 'excludeAmountZero', colspan: 2, metadata: { displayName: 'Exclude zero value bills', type: MetadataEntityFieldType.Boolean, readonly: false } })
        .field({ bind: 'isTaxInclusiveFlag', colspan: 2, metadata: { displayName: 'Set Tax Inclusive flag', type: MetadataEntityFieldType.Boolean, readonly: false } })))
    .build();
}

// Legacy Download CSV and the export filename both use DateUtility's UTC day.
// A local zone would accept a To Date the file name treats as tomorrow.
function billExportRuntime() {

  return defineSurfaceRuntime<BillExportFormModel>({
    editorInputs: {
      'bill.exportFromDate': {
        inputs: ({ page, defaultInputs }) => computed(() => ({
          ...defaultInputs,
          timezone: 'utc',
          max: billCalendarIso(page.readBind('toDate'), 'utc'),
        })),
      },
      'bill.exportToDate': {
        inputs: ({ page, defaultInputs }) => computed(() => ({
          ...defaultInputs,
          timezone: 'utc',
          min: billCalendarIso(page.readBind('fromDate'), 'utc'),
          max: billCalendarIso(new Date(), 'utc'),
        })),
      },
    },
  });
}

function defaultBillExportModel(): BillExportFormModel {

  const today = DateUtility.getDateTime(new Date()).startOf('day');

  return {
    exportFormat: ExportFormat.MYOB,
    fromDate: today.minus({ days: 30 }).toJSDate(),
    toDate: today.toJSDate(),
    company: null,
    contacts: [],
    excludeAmountZero: true,
    isTaxInclusiveFlag: true,
  };
}

function billExportRequest(model: BillExportFormModel | undefined): InvoicingCSVDownloadRequest {

  if (!model?.fromDate || !model.toDate) {
    throw new Error('From Date and To Date are required');
  }
  const fromDate = DateUtility.getDate(model.fromDate);
  const toDate = DateUtility.getDate(model.toDate);
  const fromDay = billCalendarIso(fromDate, 'utc');
  const toDay = billCalendarIso(toDate, 'utc');
  const today = billCalendarIso(new Date(), 'utc');
  if (!fromDay || !toDay) {
    throw new Error('From Date and To Date are required');
  }
  if (today && toDay > today) {
    throw new Error('To Date cannot be after today');
  }
  if (fromDay > toDay) {
    throw new Error('From Date cannot be after To Date');
  }

  return {
    contacts: model.contacts ?? [],
    exportFormat: model.exportFormat,
    fromDate,
    toDate,
    // The legacy request uses 'Invoice' to select a finance template, including for Bills.
    type: 'Invoice',
    excludeAmountZero: model.excludeAmountZero,
    isTaxInclusiveFlag: model.isTaxInclusiveFlag,
    company: model.company ?? null,
  } as InvoicingCSVDownloadRequest;
}
